import React, { useEffect, useState, useCallback } from 'react';
import { Typography, Box, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, Checkbox, FormControlLabel, Select, MenuItem, InputLabel, FormControl, SelectChangeEvent, FormGroup, Stack, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../components/AuthContext.tsx';
import { authenticatedApiCall } from '../api.ts';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  is_active: boolean;
  is_admin: boolean;
  has_car: boolean;
  car_motorization: string | null;
  has_motorcycle: boolean;
  needs_pmr: boolean;
  personal_constraints: string | null;
}

function UserManagement() {
  const { token, isAdmin } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Maintenance cleanup state
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState<string | null>(null);
  const [maintenanceResult, setMaintenanceResult] = useState<null | {
    deleted_assignments_without_spot: number;
    deleted_assignments_without_user: number;
    deleted_motorcycle_checkins_without_user: number;
  }>(null);

  // State for filters
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [motorizationFilter, setMotorizationFilter] = useState('all');
  const [pmrFilter, setPmrFilter] = useState('all');

  // State for editing
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authenticatedApiCall('GET', '/users/', token);
      setUsers(data);
    } catch (err: any) {
      setError(err.message || 'Échec de la récupération des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAdmin && token) {
      fetchUsers();
    } else if (!isAdmin) {
      setError("Vous n'avez pas les droits d'administration pour voir cette page.");
      setLoading(false);
    }
  }, [token, isAdmin, fetchUsers]);

  const handleMaintenanceCleanup = async () => {
    if (!token) return;
    setMaintenanceError(null);
    setMaintenanceLoading(true);
    try {
      const res = await authenticatedApiCall('POST', '/maintenance/cleanup', token);
      setMaintenanceResult(res);
    } catch (err: any) {
      setMaintenanceError(err.message || 'Échec du nettoyage de maintenance');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      try {
        await authenticatedApiCall('DELETE', `/users/${userId}`, token);
        fetchUsers(); // Refresh the list
      } catch (err: any) {
        setError(err.message || "Échec de la suppression de l'utilisateur");
      }
    }
  };

  const handleOpenEditModal = (user: User) => {
    setEditingUser({ ...user }); // Create a copy to edit
    setIsModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditingUser(null);
    setIsModalOpen(false);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      const { id, ...updateData } = editingUser;
      await authenticatedApiCall('PUT', `/users/${id}`, token, updateData);
      handleCloseEditModal();
      fetchUsers();
    } catch (err: any) {
      // Ideally, show this error in the modal
      console.error("Failed to update user", err);
      setError(err.message || "Échec de la mise à jour de l'utilisateur");
    }
  };

  const handleEditFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!editingUser) return;
    const { name, value, type, checked } = event.target;
    setEditingUser({
      ...editingUser,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    if (!editingUser) return;
    const { name, value } = event.target;
    setEditingUser({
      ...editingUser,
      [name]: value,
    });
  };

  const filteredUsers = users.filter(user => {
    const vehicleMatch =
      vehicleFilter === 'all' ||
      (vehicleFilter === 'has_car' && user.has_car) ||
      (vehicleFilter === 'has_motorcycle' && user.has_motorcycle);

    const motorizationMatch =
      vehicleFilter !== 'has_car' || motorizationFilter === 'all' || user.car_motorization === motorizationFilter;

    const pmrMatch =
      pmrFilter === 'all' ||
      (pmrFilter === 'yes' && user.needs_pmr) ||
      (pmrFilter === 'no' && !user.needs_pmr);

    return vehicleMatch && motorizationMatch && pmrMatch;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Gestion des Utilisateurs
      </Typography>

      {/* Maintenance (admin only) */}
      {isAdmin && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6">Maintenance des données</Typography>
              <Typography variant="body2" color="text.secondary">Supprimer les enregistrements orphelins (réservations/checkins).</Typography>
            </Box>
            <Button variant="contained" onClick={handleMaintenanceCleanup} disabled={maintenanceLoading}>
              {maintenanceLoading ? 'Nettoyage…' : 'Lancer le nettoyage'}
            </Button>
          </Stack>
          {maintenanceError && (
            <Alert severity="error" sx={{ mt: 2 }}>{maintenanceError}</Alert>
          )}
          {maintenanceResult && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1">Résultat du nettoyage</Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableBody>
                  <TableRow>
                    <TableCell>Réservations sans place</TableCell>
                    <TableCell>{maintenanceResult.deleted_assignments_without_spot}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Réservations sans utilisateur</TableCell>
                    <TableCell>{maintenanceResult.deleted_assignments_without_user}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Checkins moto sans utilisateur</TableCell>
                    <TableCell>{maintenanceResult.deleted_motorcycle_checkins_without_user}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Filtrer par véhicule</InputLabel>
          <Select
            value={vehicleFilter}
            label="Filtrer par véhicule"
            onChange={(e) => setVehicleFilter(e.target.value)}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="has_car">Voiture uniquement</MenuItem>
            <MenuItem value="has_motorcycle">Moto uniquement</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }} disabled={vehicleFilter !== 'has_car'}>
          <InputLabel>Motorisation</InputLabel>
          <Select
            value={motorizationFilter}
            label="Motorisation"
            onChange={(e) => setMotorizationFilter(e.target.value)}
          >
            <MenuItem value="all">Toutes</MenuItem>
            <MenuItem value="Thermique">Thermique</MenuItem>
            <MenuItem value="Hybride">Hybride</MenuItem>
            <MenuItem value="Electrique">Électrique</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Besoin PMR</InputLabel>
          <Select value={pmrFilter} label="Besoin PMR" onChange={(e) => setPmrFilter(e.target.value)}>
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="yes">Oui</MenuItem>
            <MenuItem value="no">Non</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="users table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Prénom</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Actif</TableCell>
              <TableCell>Admin</TableCell>
              <TableCell>Voiture</TableCell>
              <TableCell>Moto</TableCell>
              <TableCell>Besoin PMR</TableCell>
              <TableCell>Contraintes</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.first_name}</TableCell>
                <TableCell>{user.last_name}</TableCell>
                <TableCell>{user.email || 'Non fourni'}</TableCell>
                <TableCell>{user.is_active ? 'Oui' : 'Non'}</TableCell>
                <TableCell>{user.is_admin ? 'Oui' : 'Non'}</TableCell>
                <TableCell>{user.has_car ? user.car_motorization || 'Oui' : 'Non'}</TableCell>
                <TableCell>{user.has_motorcycle ? 'Oui' : 'Non'}</TableCell>
                <TableCell>{user.needs_pmr ? 'Oui' : 'Non'}</TableCell>
                <TableCell>{user.personal_constraints || 'Aucune'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenEditModal(user)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteUser(user.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit User Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseEditModal} fullWidth maxWidth="sm">
        <DialogTitle>Modifier l'utilisateur</DialogTitle>
        <DialogContent>
          {editingUser && (
            <Box component="form" sx={{ mt: 1 }}>
              <TextField
                margin="dense"
                label="Prénom"
                name="first_name"
                fullWidth
                value={editingUser.first_name}
                onChange={handleEditFormChange}
              />
              <TextField
                margin="dense"
                label="Nom"
                name="last_name"
                fullWidth
                value={editingUser.last_name}
                onChange={handleEditFormChange}
              />
              <TextField
                margin="dense"
                label="Adresse e-mail"
                name="email"
                type="email"
                fullWidth
                value={editingUser.email || ''}
                onChange={handleEditFormChange}
              />
              <FormGroup sx={{ mt: 2 }}>
                <FormControlLabel
                  control={<Checkbox name="has_car" checked={editingUser.has_car} onChange={handleEditFormChange} />}
                  label="Possède une voiture"
                />
                {editingUser.has_car && (
                  <FormControl fullWidth margin="dense" sx={{ pl: 4 }}>
                    <InputLabel id="edit-car-motorization-label">Motorisation</InputLabel>
                    <Select
                      labelId="edit-car-motorization-label"
                      name="car_motorization"
                      value={editingUser.car_motorization || ''}
                      label="Motorisation"
                      onChange={handleSelectChange}
                    >
                      <MenuItem value=""><em>Non spécifié</em></MenuItem>
                      <MenuItem value="Thermique">Thermique</MenuItem>
                      <MenuItem value="Hybride">Hybride</MenuItem>
                      <MenuItem value="Electrique">Électrique</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <FormControlLabel
                  control={<Checkbox name="has_motorcycle" checked={editingUser.has_motorcycle} onChange={handleEditFormChange} />}
                  label="Possède une moto"
                />
              </FormGroup>
              <TextField
                margin="dense"
                label="Contraintes personnelles"
                name="personal_constraints"
                fullWidth
                multiline
                rows={2}
                value={editingUser.personal_constraints || ''}
                onChange={handleEditFormChange}
              />
              <FormControlLabel
                control={<Checkbox name="needs_pmr" checked={editingUser.needs_pmr} onChange={handleEditFormChange} />}
                label="Besoin PMR"
              />
              <FormControlLabel
                control={<Checkbox name="is_admin" checked={editingUser.is_admin} onChange={handleEditFormChange} />}
                label="Administrateur"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal}>Annuler</Button>
          <Button onClick={handleUpdateUser} variant="contained">Sauvegarder</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;
